import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Settings, Phone, Check, ArrowRight, Shield, Clock, Euro, AlertTriangle } from 'lucide-react';

export default function ServiceTechnique() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const { API } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nom: '', entreprise: '', telephone: '', email: '', type_service: '',
    description_besoin: '', lieu_intervention: '', urgence: false, budget: '', rgpd_accepted: false,
  });

  const typesServices = [
    { value: 'etalonnage', label: isFr ? 'Étalonnage / Calibration' : 'Calibration' },
    { value: 'maintenance', label: isFr ? 'Maintenance préventive' : 'Preventive maintenance' },
    { value: 'reparation', label: isFr ? 'Réparation / Dépannage' : 'Repair / Troubleshooting' },
    { value: 'formation', label: isFr ? 'Formation opérateur' : 'Operator training' },
    { value: 'audit', label: isFr ? 'Audit technique' : 'Technical audit' },
    { value: 'installation', label: isFr ? 'Installation / Mise en service' : 'Installation / Commissioning' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.rgpd_accepted) { toast.error(isFr ? 'Veuillez accepter les conditions' : 'Please accept conditions'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.keys(formData).forEach(k => { if (formData[k] !== '' && formData[k] !== null) fd.append(k, formData[k]); });
      await axios.post(`${API}/demandes/service`, fd);
      setSubmitted(true);
      toast.success(isFr ? 'Demande envoyée !' : 'Request sent!');
    } catch (error) { toast.error(isFr ? 'Erreur' : 'Error'); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-green-600" /></div>
          <h1 className="text-3xl font-bold text-[#0A2540] mb-4">{isFr ? 'Merci !' : 'Thank you!'}</h1>
          <p className="text-gray-600 mb-6">{isFr ? 'Nous recherchons le meilleur prestataire pour vous. Réponse sous 24h.' : 'We\'re finding the best provider for you. Response within 24h.'}</p>
          <div className="flex items-center justify-center gap-2 text-[#635BFF] font-medium"><Phone className="w-5 h-5" /><span>+33 7 82 93 93 83</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="pt-16 pb-8 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 rounded-full px-4 py-2 mb-6">
            <Settings className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-600">{isFr ? 'Service technique' : 'Technical service'}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">{isFr ? 'Trouvez un prestataire technique' : 'Find a technical provider'}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{isFr ? 'Étalonnage, maintenance, formation, réparation... On vous met en relation avec les meilleurs experts.' : 'Calibration, maintenance, training, repair... We connect you with the best experts.'}</p>
        </div>
      </section>

      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-2xl">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{isFr ? 'Nom complet' : 'Full name'} *</Label><Input required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} className="mt-2" /></div>
                <div><Label>{isFr ? 'Entreprise' : 'Company'}</Label><Input value={formData.entreprise} onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })} className="mt-2" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{isFr ? 'Téléphone' : 'Phone'} *</Label><Input type="tel" required value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} className="mt-2" /></div>
                <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-2" /></div>
              </div>
              <div>
                <Label>{isFr ? 'Type de service' : 'Service type'} *</Label>
                <Select value={formData.type_service} onValueChange={(v) => setFormData({ ...formData, type_service: v })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder={isFr ? 'Sélectionnez' : 'Select'} /></SelectTrigger>
                  <SelectContent>{typesServices.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>{isFr ? 'Description du besoin' : 'Description'} *</Label><Textarea required value={formData.description_besoin} onChange={(e) => setFormData({ ...formData, description_besoin: e.target.value })} placeholder={isFr ? 'Décrivez votre besoin...' : 'Describe your need...'} className="mt-2" rows={3} /></div>
              <div><Label>{isFr ? "Lieu d'intervention" : 'Location'} *</Label><Input required value={formData.lieu_intervention} onChange={(e) => setFormData({ ...formData, lieu_intervention: e.target.value })} placeholder="Lyon, France" className="mt-2" /></div>
              <div><Label>{isFr ? 'Budget approximatif' : 'Approximate budget'}</Label><Input value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} placeholder="2000€" className="mt-2" /></div>
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
                <Checkbox id="urgence" checked={formData.urgence} onCheckedChange={(c) => setFormData({ ...formData, urgence: c })} />
                <Label htmlFor="urgence" className="flex items-center gap-2 cursor-pointer"><AlertTriangle className="w-4 h-4 text-amber-600" />{isFr ? 'Intervention urgente' : 'Urgent intervention'}</Label>
              </div>
              <div className="pt-4 border-t">
                <div className="flex items-start gap-3">
                  <Checkbox id="rgpd" checked={formData.rgpd_accepted} onCheckedChange={(c) => setFormData({ ...formData, rgpd_accepted: c })} />
                  <Label htmlFor="rgpd" className="text-sm text-gray-600 cursor-pointer">{isFr ? "J'accepte le traitement de mes données (RGPD)" : "I accept data processing (GDPR)"}</Label>
                </div>
              </div>
              <Button type="submit" disabled={loading || !formData.rgpd_accepted} className="w-full bg-green-600 hover:bg-green-700 text-white py-4 text-lg">
                {loading ? <div className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>{isFr ? 'Envoi...' : 'Sending...'}</div> : <span className="flex items-center gap-2">{isFr ? 'Trouver un prestataire' : 'Find a provider'}<ArrowRight className="w-5 h-5" /></span>}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
